import AddItemProcessing from 'components/menu-page/AddItemProcessing';
import { useItemSheet } from 'hooks/use-item-sheet';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useStores } from 'hooks/use-stores';
import {
  trackAddSharedCartItemFailure,
  trackAddSharedCartItemSuccess,
  trackClickAddSharedCartItem,
} from 'integrations/segment/tracking-events/SharedCarts';
import { formatSharedCartItemForTracking } from 'integrations/segment/util/formatting/SharedCart';
import {
  trackAddToCartFailure,
  trackAddToCartSuccess,
  trackClickAddToCart,
  trackViewMenuItem,
} from 'integrations/segment/tracking-events/itemModal';
import { formatCartItemForTracking } from 'integrations/segment/util/formatting/Cart';
import { formatMenuItemForTracking } from 'integrations/segment/util/formatting/Menu';
import { trackGTMEcommViewItem } from 'integrations/google-tag-manager/tracking-events';
import useQuery from 'hooks/use-query';
import CartItemModalContent from './CartItemModalContent';
import { SaveStatus } from './constants';

function CartItemAddModal() {
  const { checkoutStore } = useStores();
  const { selectedCart } = checkoutStore;
  const query = useQuery();
  const fromCarousel = !!query.get('fromCarousel');
  const {
    processing,
    setProcessing,
    setSubmitPressed,
    cartItem,
    displayedItem,
    setDisplayedItem,
    htmlElementErrors,
    modsWithMissingSelections,
    closeModal,
  } = useItemSheet();
  const { menuItem, menu } = cartItem;

  useEffect(() => {
    trackViewMenuItem({ ...formatMenuItemForTracking(menuItem, menu) });
    trackGTMEcommViewItem(cartItem);
  }, [cartItem]);

  const handleAddToCart = () => {
    if (!displayedItem.isValid()) {
      setSubmitPressed(true);
      return SaveStatus.Error;
    }

    if (displayedItem.parent) {
      setDisplayedItem(cartItem);
      return SaveStatus.ModifierUpdated;
    }

    cartItem.clean();
    selectedCart.handleAddItemToCart(cartItem);
    return SaveStatus.Success;
  };

  const handleSubmit = () => {
    const formattedCartForTracking = { ...formatCartItemForTracking(cartItem), from_most_loved_carousel: fromCarousel };
    const formattedSharedCartForTracking = {
      ...formatSharedCartItemForTracking(cartItem),
      from_most_loved_carousel: fromCarousel,
    };
    const editingNestedModifier = Boolean(displayedItem.parent);

    setProcessing(!editingNestedModifier);

    selectedCart.isSharedCart
      ? trackClickAddSharedCartItem(formattedSharedCartForTracking)
      : trackClickAddToCart(formattedCartForTracking);

    const saveStatus = handleAddToCart();
    // Close the CartItemModal when the cartItem has been updated without errors.
    if (saveStatus === SaveStatus.Success) {
      selectedCart.isSharedCart
        ? trackAddSharedCartItemSuccess(formattedCartForTracking)
        : trackAddToCartSuccess(formattedCartForTracking);

      setProcessing(false);
      closeModal();
      return;
    }

    if (saveStatus === SaveStatus.Error || modsWithMissingSelections.length) {
      selectedCart?.isSharedCart
        ? trackAddSharedCartItemFailure(formattedSharedCartForTracking)
        : trackAddToCartFailure(formattedCartForTracking);
    }
    // Require the user to resolve all errors highlighted in the current modifier.
    if (htmlElementErrors.length) {
      htmlElementErrors[0].scrollIntoView(true);
      htmlElementErrors[0].querySelector('input')?.focus();
    }
    // Require the user to resolve errors in any other modifier not currently selected.
    else if (modsWithMissingSelections.length) {
      setDisplayedItem(modsWithMissingSelections[0]);
    }

    setProcessing(false);
  };

  return processing ? <AddItemProcessing /> : <CartItemModalContent handleSubmit={handleSubmit} isNew />;
}

export default observer(CartItemAddModal);
